import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { 
  Home, MapPin, Euro, Bed, Bath, Maximize, Plus, Trash2, Edit, Eye, 
  TrendingUp, Percent, PieChart, BarChart3, Calendar, Building, 
  ChevronRight, AlertCircle, CheckCircle, Clock, Crown, RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper para obtener la URL de la imagen principal
const getMainImageUrl = (images) => {
  if (!images || images.length === 0) return null;
  
  // Si es array de objetos
  if (typeof images[0] === 'object') {
    const portada = images.find(img => img.category === 'portada');
    const url = portada?.url || images[0]?.url;
    if (url) {
      return url.startsWith('/') ? `${BACKEND_URL}${url}` : url;
    }
  }
  // Si es array de strings
  if (typeof images[0] === 'string') {
    const url = images[0];
    return url.startsWith('/') ? `${BACKEND_URL}${url}` : url;
  }
  return null;
};

// Helper para obtener array de URLs de imágenes
const getImageUrls = (images) => {
  if (!images || images.length === 0) return [];
  
  return images.map(img => {
    let url = typeof img === 'object' ? img.url : img;
    if (url && url.startsWith('/')) {
      return `${BACKEND_URL}${url}`;
    }
    return url;
  }).filter(url => url);
};

export const MyProperties = () => {
  const { user, dataVersion } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchProperties();
  }, [dataVersion]); // Recargar cuando cambie dataVersion (login/logout)

  const fetchProperties = async () => {
    try {
      const timestamp = new Date().getTime();
      const response = await axios.get(`${API}/my-properties?t=${timestamp}`, { 
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Error al cargar tus propiedades');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (propertyId) => {
    try {
      await axios.delete(`${API}/my-properties/${propertyId}`);
      setProperties(properties.filter(p => p.property_id !== propertyId));
      setShowDeleteConfirm(null);
      toast.success('Propiedad eliminada');
    } catch (error) {
      toast.error('Error al eliminar la propiedad');
    }
  };

  const getMarketStatusColor = (status) => {
    switch (status) {
      case 'below_market': return 'text-green-400';
      case 'above_market': return 'text-red-400';
      default: return 'text-yellow-400';
    }
  };

  const getMarketStatusLabel = (status) => {
    switch (status) {
      case 'below_market': return 'Por debajo del mercado';
      case 'above_market': return 'Por encima del mercado';
      default: return 'Precio de mercado';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <RefreshCw size={32} className="text-purple-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 p-4 md:p-8">
      <div className="max-w-7xl mx-auto pt-16 md:pt-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className={`text-3xl md:text-4xl font-bold bg-gradient-to-r from-yellow-500 to-amber-500 bg-clip-text text-transparent`}>
              Mis Propiedades
            </h1>
            <p className="text-gray-400 mt-1 text-sm md:text-base">Gestiona tus anuncios y analiza su rendimiento</p>
          </div>
          <Link 
            to="/publish"
            className={`flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-medium hover:opacity-90 transition-all`}
          >
            <Plus size={20} />
            Nueva Propiedad
          </Link>
        </div>

        {properties.length === 0 ? (
          /* Empty state */
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-12 text-center">
            <Building size={64} className="mx-auto text-gray-600 mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">No tienes propiedades publicadas</h2>
            <p className="text-gray-400 mb-6">Publica tu primera propiedad y comienza a recibir análisis detallados</p>
            <Link 
              to="/publish"
              className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-medium`}
            >
              <Plus size={20} />
              Publicar mi primera propiedad
            </Link>
          </div>
        ) : (
          <div className="grid gap-6">
            {properties.map((property) => {
              const analytics = property.analytics || {};
              const imageUrls = getImageUrls(property.images);
              
              return (
                <div 
                  key={property.property_id}
                  className="bg-gray-900 border border-white/10 rounded-2xl overflow-hidden hover:border-purple-500/30 transition-all"
                >
                  <div className="flex flex-col lg:flex-row">
                    {/* Image Carousel */}
                    <div className="lg:w-80 h-64 lg:h-80 relative flex-shrink-0 bg-gray-800">
                      {imageUrls.length > 0 ? (
                        <Carousel
                          showArrows={imageUrls.length > 1}
                          showThumbs={false}
                          infiniteLoop={true}
                          autoPlay={false}
                          showStatus={imageUrls.length > 1}
                          className="property-carousel h-full"
                        >
                          {imageUrls.map((imgUrl, idx) => (
                            <div key={idx} className="h-64 lg:h-80 bg-gray-900 flex items-center justify-center">
                              <img 
                                src={imgUrl} 
                                alt={`${property.title} - ${idx + 1}`}
                                className="w-full h-full object-contain"
                                onError={(e) => { 
                                  e.target.onerror = null; 
                                  e.target.src = ''; 
                                  e.target.parentElement.innerHTML = '<div class="w-full h-full bg-gray-800 flex items-center justify-center"><svg class="w-12 h-12 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path></svg></div>';
                                }}
                              />
                            </div>
                          ))}
                        </Carousel>
                      ) : (
                        <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                          <Home size={48} className="text-gray-600" />
                        </div>
                      )}
                      <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold z-10 ${
                        property.status === 'active' ? 'bg-green-500 text-white' : 'bg-gray-600 text-gray-200'
                      }`}>
                        {property.status === 'active' ? 'Activo' : 'Inactivo'}
                      </div>
                      <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-black/60 text-white text-xs font-medium capitalize z-10">
                        {property.listing_type}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 p-5">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="text-xl font-bold text-white mb-1">{property.title}</h3>
                          <p className="text-gray-400 text-sm flex items-center gap-1">
                            <MapPin size={14} />
                            {property.location}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`text-2xl font-bold bg-gradient-to-r from-yellow-500 to-amber-500 bg-clip-text text-transparent`}>
                            {parseInt(property.price).toLocaleString('es-ES')} €
                          </p>
                          {property.listing_type !== 'venta' && <span className="text-gray-500 text-sm">/mes</span>}
                        </div>
                      </div>

                      {/* Property details */}
                      <div className="flex flex-wrap gap-4 text-sm text-gray-400 mb-4">
                        <span className="flex items-center gap-1"><Bed size={16} /> {property.bedrooms} hab</span>
                        <span className="flex items-center gap-1"><Bath size={16} /> {property.bathrooms} baños</span>
                        <span className="flex items-center gap-1"><Maximize size={16} /> {property.area} m²</span>
                        <span className="flex items-center gap-1 capitalize"><Building size={16} /> {property.property_type}</span>
                        <span className="flex items-center gap-1">
                          <Calendar size={16} /> 
                          {new Date(property.created_at).toLocaleDateString('es-ES')}
                        </span>
                      </div>

                      {/* Analytics section */}
                      {analytics && Object.keys(analytics).length > 0 && (
                        <div className="bg-gray-800/50 rounded-xl p-4 mb-4">
                          <div className="flex items-center gap-2 mb-3">
                            <BarChart3 size={18} className="text-purple-400" />
                            <span className="text-sm font-semibold text-white">Análisis de Inversión</span>
                            {user?.subscription_plan === 'premium' && (
                              <Crown size={14} className="text-yellow-400" />
                            )}
                          </div>
                          
                          {/* Blur overlay for free users */}
                          {(!user?.subscription_plan || user?.subscription_plan === 'usuario' || user?.subscription_plan === 'basic') ? (
                            <div className="relative">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" style={{ filter: 'blur(8px)', pointerEvents: 'none' }}>
                                {/* Blurred preview */}
                                <div className="bg-gray-900/50 rounded-lg p-3">
                                  <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                                    <Percent size={12} />
                                    ROI Neto
                                  </div>
                                  <p className="text-lg font-bold text-green-400">7.2%</p>
                                </div>
                                <div className="bg-gray-900/50 rounded-lg p-3">
                                  <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                                    <Euro size={12} />
                                    €/m²
                                  </div>
                                  <p className="text-lg font-bold text-white">2,350</p>
                                </div>
                                <div className="bg-gray-900/50 rounded-lg p-3">
                                  <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                                    <PieChart size={12} />
                                    Precio
                                  </div>
                                  <p className="text-sm font-semibold text-green-400">Por debajo</p>
                                </div>
                                <div className="bg-gray-900/50 rounded-lg p-3">
                                  <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                                    <TrendingUp size={12} />
                                    Revalorización
                                  </div>
                                  <p className="text-lg font-bold text-green-400">+18%</p>
                                </div>
                              </div>
                              
                              {/* Upgrade overlay */}
                              <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80 rounded-lg">
                                <div className="text-center px-4">
                                  <Crown size={32} className="mx-auto text-purple-400 mb-2" />
                                  <p className="text-white font-bold mb-2">Datos IA Exclusivos</p>
                                  <p className="text-gray-400 text-xs mb-4">Actualiza a Avanzado para ver ROI, plusvalía y más</p>
                                  <Link
                                    to="/select-plan"
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-medium hover:opacity-90 text-sm"
                                  >
                                    Ver planes
                                    <ChevronRight size={16} />
                                  </Link>
                                </div>
                              </div>
                            </div>
                          ) : (
                            /* Clear data for Essential/Premium users */
                            <>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {/* ROI */}
                                <div className="bg-gray-900/50 rounded-lg p-3">
                                  <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                                    <Percent size={12} />
                                    ROI Neto
                                  </div>
                                  <p className={`text-lg font-bold ${analytics.roi >= 5 ? 'text-green-400' : analytics.roi >= 3 ? 'text-yellow-400' : 'text-red-400'}`}>
                                    {analytics.roi || '—'}%
                                  </p>
                                </div>

                                {/* Price per m2 */}
                                <div className="bg-gray-900/50 rounded-lg p-3">
                                  <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                                    <Euro size={12} />
                                    €/m²
                                  </div>
                                  <p className="text-lg font-bold text-white">
                                    {analytics.price_per_m2?.toLocaleString('es-ES') || '—'}
                                  </p>
                                </div>

                                {/* Market status */}
                                <div className="bg-gray-900/50 rounded-lg p-3">
                                  <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                                    <PieChart size={12} />
                                    Precio
                                  </div>
                                  <p className={`text-sm font-semibold ${getMarketStatusColor(analytics.market_price_status)}`}>
                                    {getMarketStatusLabel(analytics.market_price_status)}
                                  </p>
                                </div>

                                {/* Appreciation 5y */}
                                <div className="bg-gray-900/50 rounded-lg p-3">
                                  <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                                    <TrendingUp size={12} />
                                    Revalorización 5 años
                                  </div>
                                  <p className="text-lg font-bold text-green-400">
                                    +{analytics.appreciation_5y || '—'}%
                                  </p>
                                </div>
                              </div>

                              {/* Additional premium analytics */}
                              {['profesional', 'premium'].includes(user?.subscription_plan) && (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 pt-3 border-t border-white/5">
                                  <div>
                                    <p className="text-xs text-gray-500">Revalorización 10 años</p>
                                    <p className="text-sm font-semibold text-green-400">+{analytics.appreciation_10y || '—'}%</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">Plusvalía Municipal</p>
                                    <p className="text-sm font-semibold text-orange-400">{analytics.plusvalia_municipal?.toLocaleString('es-ES') || '—'} €</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">ITP (Impuesto)</p>
                                    <p className="text-sm font-semibold text-blue-400">{analytics.itp_tax?.toLocaleString('es-ES') || '—'} €</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">Gastos Notariales</p>
                                    <p className="text-sm font-semibold text-purple-400">{analytics.notary_fees?.toLocaleString('es-ES') || '—'} €</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">Gastos Registro</p>
                                    <p className="text-sm font-semibold text-indigo-400">{analytics.registry_fees?.toLocaleString('es-ES') || '—'} €</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">Total Gastos Compraventa</p>
                                    <p className="text-sm font-semibold text-red-400">{analytics.total_transaction_costs?.toLocaleString('es-ES') || '—'} €</p>
                                  </div>
                                  {analytics.capital_gain && (
                                    <div>
                                      <p className="text-xs text-gray-500">Ganancia Patrimonial</p>
                                      <p className="text-sm font-semibold text-green-300">{analytics.capital_gain?.toLocaleString('es-ES')} €</p>
                                    </div>
                                  )}
                                  <div>
                                    <p className="text-xs text-gray-500">Gastos mensuales</p>
                                    <p className="text-sm font-semibold text-white">{analytics.monthly_costs || '—'} €</p>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex flex-wrap gap-2">
                        <Link
                          to={`/property/${property.property_id}`}
                          className="flex items-center gap-1 px-4 py-2 rounded-lg bg-gray-800 text-gray-300 hover:bg-gray-700 text-sm transition-colors"
                        >
                          <Eye size={16} />
                          Ver anuncio
                        </Link>
                        <button
                          onClick={() => navigate(`/edit-property/${property.property_id}`)}
                          className="flex items-center gap-1 px-4 py-2 rounded-lg bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 text-sm transition-colors"
                        >
                          <Edit size={16} />
                          Editar
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(property.property_id)}
                          className="flex items-center gap-1 px-4 py-2 rounded-lg bg-red-500/20 text-red-300 hover:bg-red-500/30 text-sm transition-colors"
                        >
                          <Trash2 size={16} />
                          Eliminar
                        </button>
                      </div>

                      {/* Delete confirmation */}
                      {showDeleteConfirm === property.property_id && (
                        <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                          <p className="text-sm text-red-300 mb-3">¿Estás seguro de que quieres eliminar esta propiedad?</p>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleDelete(property.property_id)}
                              className="px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600"
                            >
                              Sí, eliminar
                            </button>
                            <button
                              onClick={() => setShowDeleteConfirm(null)}
                              className="px-4 py-2 rounded-lg bg-gray-700 text-gray-300 text-sm hover:bg-gray-600"
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ROI Explanation for non-premium */}
        {!['profesional', 'premium'].includes(user?.subscription_plan) && properties.length > 0 && (
          <div className="mt-8 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <Crown size={32} className="text-yellow-400 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Desbloquea más análisis con Profesional</h3>
                <p className="text-gray-400 text-sm mb-4">
                  Con el plan Profesional tendrás acceso a análisis avanzados: ROI bruto, proyección a 10 años,
                  gastos detallados, plusvalía municipal y mucho más.
                </p>
                <Link
                  to="/select-plan"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-500 text-black font-medium hover:bg-yellow-400 text-sm"
                >
                  Ver Planes
                  <ChevronRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
